// ADMIN-INSIGHTS in the in-memory API: Ask (`insights.catalog|query|ask|asks`) and pinned answers
// (`insights.widgets.list|create|delete`) with the real routes' rules — owners, admins and agency
// operators only (403 to anyone else); a breakdown the measure does not offer is a 422; rules map
// a question (no model here); pins are the reader's own or shared with every admin, unpinned by
// their owner (or any admin for a shared one). Numbers come from the twin's own records: the Work
// twin's tasks and the org chart's seat holders, the Finance twin's agent spend and budgets, the
// approvals list — nothing invented. Calendar maths is UTC (the twin's companies all are).
import type { OperationInput, OperationOutput, RouteId } from "@companyos/sdk";
import { type Ctx, json, problem, validation } from "./fake";
import { uid } from "./fake-data";
import type { ComposedHandler } from "./fake-merge";
import type { InternalFetch } from "./fake-palette";
import type { StoredTask } from "./fake-work-data";

type Result = OperationOutput<"insights.query">;
type Spec = Result["spec"];
type Row = Result["rows"][number];
type Answer = OperationOutput<"insights.ask">;
type Widget = OperationOutput<"insights.widgets.create">;
type Measure = Spec["measure"];
type By = Spec["by"];
type Period = Spec["period"];

const CATALOG: Record<
  Measure,
  {
    label: string;
    unit: Result["unit"];
    kind: "flow" | "state";
    by: By[];
    info: string;
    link: string;
  }
> = {
  agent_spend: {
    label: "Agent spend",
    unit: "money",
    kind: "flow",
    by: ["none", "agent", "day", "week"],
    info: "What the DEs and agents cost, from the cost ledger.",
    link: "/kpis/reports/finance/agent-spend",
  },
  budget_use: {
    label: "Budget used",
    unit: "percent",
    kind: "state",
    by: ["agent"],
    info: "Each DE's share of its budget used this month.",
    link: "/kpis/reports/finance/agent-spend",
  },
  tasks_done: {
    label: "Tasks done",
    unit: "count",
    kind: "flow",
    by: ["none", "assignee_kind", "assignee", "day", "week"],
    info: "Tasks finished in the period.",
    link: "/work",
  },
  tasks_created: {
    label: "Tasks created",
    unit: "count",
    kind: "flow",
    by: ["none", "assignee_kind", "assignee", "day", "week"],
    info: "Tasks created in the period.",
    link: "/work",
  },
  tasks_open: {
    label: "Open tasks",
    unit: "count",
    kind: "state",
    by: ["none", "assignee_kind", "assignee", "day", "week"],
    info: "Tasks not done yet.",
    link: "/work",
  },
  tasks_in_progress: {
    label: "Tasks in progress",
    unit: "count",
    kind: "state",
    by: ["none", "assignee_kind", "assignee", "day", "week"],
    info: "Open tasks in a started status.",
    link: "/work",
  },
  tasks_overdue: {
    label: "Overdue tasks",
    unit: "count",
    kind: "state",
    by: ["none", "assignee_kind", "assignee", "day", "week"],
    info: "Open tasks past their due date.",
    link: "/work",
  },
  approvals_waiting: {
    label: "Approvals waiting",
    unit: "count",
    kind: "state",
    by: ["none", "day", "week"],
    info: "Approvals still waiting for a person.",
    link: "/inbox",
  },
};
const PERIODS: Period[] = [
  "today",
  "this_week",
  "last_week",
  "this_month",
  "last_month",
  "last_30_days",
  "this_quarter",
];
const PERIOD_LABEL: Record<Period, string> = {
  today: "today",
  this_week: "this week",
  last_week: "last week",
  this_month: "this month",
  last_month: "last month",
  last_30_days: "the last 30 days",
  this_quarter: "this quarter",
};
const BY_LABEL: Record<By, string> = {
  none: "total",
  agent: "per DE / agent",
  assignee_kind: "people vs DEs vs agents",
  assignee: "per assignee",
  day: "per day",
  week: "per week",
};
const EXAMPLES = [
  "Agent spend this month per DE",
  "Tasks done this week: people vs DEs vs agents",
  "Which DEs are close to their budget?",
  "Overdue tasks by assignee",
  "Tasks done per week this quarter",
  "Approvals waiting",
];
const KIND_LABEL = { person: "People", de: "DEs", ag: "Agents (AG)", unassigned: "Unassigned" };

const DAY = 86_400_000;
const dayKey = (d: Date) => d.toISOString().slice(0, 10);
const startOfDay = (d: Date) =>
  new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
const startOfWeek = (d: Date) =>
  new Date(startOfDay(d).getTime() - ((d.getUTCDay() + 6) % 7) * DAY);

function windowOf(
  period: Period,
  now: Date,
): { from: Date; to: Date; prevFrom: Date; prevTo: Date } {
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  const span = (from: Date, to: Date, prevFrom: Date) => ({
    from,
    to,
    prevFrom,
    prevTo: new Date(prevFrom.getTime() + (to.getTime() - from.getTime())),
  });
  switch (period) {
    case "today":
      return span(startOfDay(now), now, new Date(startOfDay(now).getTime() - DAY));
    case "this_week":
      return span(startOfWeek(now), now, new Date(startOfWeek(now).getTime() - 7 * DAY));
    case "last_week": {
      const s = new Date(startOfWeek(now).getTime() - 7 * DAY);
      return span(s, startOfWeek(now), new Date(s.getTime() - 7 * DAY));
    }
    case "this_month":
      return span(new Date(Date.UTC(y, m, 1)), now, new Date(Date.UTC(y, m - 1, 1)));
    case "last_month":
      return span(
        new Date(Date.UTC(y, m - 1, 1)),
        new Date(Date.UTC(y, m, 1)),
        new Date(Date.UTC(y, m - 2, 1)),
      );
    case "last_30_days":
      return span(
        new Date(startOfDay(now).getTime() - 29 * DAY),
        now,
        new Date(startOfDay(now).getTime() - 59 * DAY),
      );
    case "this_quarter": {
      const q = Math.floor(m / 3) * 3;
      return span(new Date(Date.UTC(y, q, 1)), now, new Date(Date.UTC(y, q - 3, 1)));
    }
  }
}

/** The words of a question → a catalogue spec (the API's rules, condensed). */
function parse(text: string): Spec | null {
  const q = ` ${text.toLowerCase().replace(/[^a-z0-9%$ ]+/g, " ")} `;
  const has = (re: RegExp) => re.test(q);
  let measure: Measure | null = null;
  if (has(/budget|limit|pace/)) measure = "budget_use";
  else if (has(/spend|spent|cost|expens|money|\$/)) measure = "agent_spend";
  else if (has(/approval/)) measure = "approvals_waiting";
  else if (has(/overdue|late |past due/)) measure = "tasks_overdue";
  else if (has(/in progress|working on|started|ongoing/)) measure = "tasks_in_progress";
  else if (has(/ open |pending|to do|todo|outstanding|backlog/)) measure = "tasks_open";
  else if (has(/created|new task|added/)) measure = "tasks_created";
  else if (has(/done|complet|finish|closed|shipped|task|output|work/)) measure = "tasks_done";
  if (!measure) return null;
  let by: By | undefined;
  if (has(/per day|daily|by day/)) by = "day";
  else if (has(/per week|weekly|by week|trend|over time/)) by = "week";
  else if (has(/human|people|person vs| vs |versus|split/))
    by = measure === "agent_spend" ? "agent" : "assignee_kind";
  else if (has(/per |each |by |which |who /))
    by = measure === "agent_spend" || measure === "budget_use" ? "agent" : "assignee";
  let period: Period | undefined;
  if (has(/today/)) period = "today";
  else if (has(/last week/)) period = "last_week";
  else if (has(/last month/)) period = "last_month";
  else if (has(/30 days/)) period = "last_30_days";
  else if (has(/quarter/)) period = "this_quarter";
  else if (has(/month/)) period = "this_month";
  else if (has(/week/)) period = "this_week";
  return normal({
    measure,
    by,
    period:
      period ?? (by === "week" ? "this_quarter" : by === "day" ? "last_30_days" : "this_week"),
  });
}

function normal(s: { measure: Measure; by?: By | undefined; period?: Period | undefined }): Spec {
  const def = CATALOG[s.measure];
  return {
    measure: s.measure,
    by: s.by && def.by.includes(s.by) ? s.by : (def.by[0] as By), // "none" → the default
    period: s.period ?? "this_week",
  };
}

function titleOf(s: Spec): string {
  const def = CATALOG[s.measure];
  const by = s.by === "none" ? "" : ` (${BY_LABEL[s.by]})`;
  const history = s.by === "day" || s.by === "week";
  if (def.kind === "state" && !history)
    return `${def.label}${by}, ${s.measure === "budget_use" ? "current budgets" : "now"}`;
  return `${def.label}${by}, ${PERIOD_LABEL[s.period]}`;
}

type Holder = { membership_id: string; display_name: string; membership_kind: string };
type SpendRow = OperationOutput<"finance.agentSpend">["rows"][number];

export function createInsightsFake(deps: {
  now: () => Date;
  internal: InternalFetch;
  tasksOf: (companyId: string) => StoredTask[];
}) {
  let seq = 0;
  const asks: (Answer & { company_id: string; membership_id: string })[] = [];
  const widgets: (Widget & { company_id: string })[] = [];

  const meIn = (ctx: Ctx): string | null =>
    ctx.me.memberships.find(
      (m) => m.company_id === ctx.principal?.company.id && m.kind === "person",
    )?.membership_id ?? null;
  const forbidden = () => problem(403, "forbidden", "Forbidden", "Owners and admins only.");

  async function read<T>(path: string): Promise<T | null> {
    const r = await deps.internal(path);
    return r.ok ? ((await r.json()) as T) : null;
  }

  async function holdersBySeat(co: string): Promise<Map<string, Holder[]>> {
    const chart = await read<{ seats: { id: string; holders: Holder[] }[] }>(
      `/v1/companies/${co}/org-chart`,
    );
    return new Map((chart?.seats ?? []).map((s) => [s.id, s.holders]));
  }

  const kindOf = (k: string): Row["kind"] =>
    k === "person" ? "person" : k === "digital_employee" ? "de" : "ag";

  async function query(co: string, input: Spec): Promise<Result> {
    const spec = normal(input);
    const def = CATALOG[spec.measure];
    const now = deps.now();
    const w = windowOf(spec.period, now);
    const history = spec.by === "day" || spec.by === "week";
    const base = {
      spec,
      title: titleOf(spec),
      unit: def.unit,
      currency: null as string | null,
      computed_at: now.toISOString(),
      link: def.link,
      note: null as string | null,
    };
    if (spec.measure === "agent_spend" || spec.measure === "budget_use") {
      const spend = await read<OperationOutput<"finance.agentSpend">>(
        `/v1/companies/${co}/finance/agent-spend`,
      );
      const rows: SpendRow[] = spend?.rows ?? [];
      const currency = spend?.currency ?? "USD";
      if (spec.measure === "budget_use") {
        const out: Row[] = rows
          .filter((r) => r.budget)
          .map((r) => ({
            key: `member:${r.membership_id}`,
            label: r.name,
            kind: "de" as const,
            value: Math.round((r.budget?.used ?? 0) * 1000) / 10,
            detail: `of ${new Intl.NumberFormat("en-US", { style: "currency", currency }).format((r.budget?.limit_minor ?? 0) / 100)}`,
          }))
          .sort((a, b) => b.value - a.value);
        return {
          ...base,
          from: dayKey(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))),
          to: dayKey(now),
          total: null,
          previous_total: null,
          rows: out,
          note: out.length === 0 ? "No DE has a budget yet." : null,
        };
      }
      // Each period from the figures that cover it (Codex: never this week's figure for last
      // week, nor this month's for the quarter): the day, week and month figures, other months
      // read by key, and the 30-day trend for windows inside it.
      const trend = spend?.trend ?? [];
      const inWindow = (from: Date, to: Date) =>
        trend.filter((t) => t.day >= dayKey(from) && t.day < dayKey(new Date(to.getTime() + 1)));
      const trendCovers = (from: Date) => trend.length > 0 && (trend[0]?.day ?? "") <= dayKey(from);
      const sumTrend = (from: Date, to: Date) =>
        trendCovers(from) ? inWindow(from, to).reduce((s, t) => s + t.amount_minor, 0) : null;
      const months = (from: Date, to: Date) => {
        const keys: string[] = [];
        for (
          let d = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), 1));
          d < to;
          d = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1))
        )
          keys.push(d.toISOString().slice(0, 7));
        return keys;
      };
      const monthly = async (from: Date, to: Date) =>
        Promise.all(
          months(from, to).map((k) =>
            read<OperationOutput<"finance.agentSpend">>(
              `/v1/companies/${co}/finance/agent-spend?period_key=${k}`,
            ),
          ),
        );
      /** Totals and per-agent figures of a window, or null where the twin has none. */
      const figures = async (
        period: Period,
        from: Date,
        to: Date,
      ): Promise<{ total: number | null; byAgent: Map<string, number> | null }> => {
        const own = (f: (x: SpendRow) => number) =>
          new Map(rows.map((r) => [r.membership_id, f(r)]));
        if (period === "today")
          return { total: spend?.totals.today_minor ?? 0, byAgent: own((r) => r.today_minor) };
        if (period === "this_week")
          return { total: spend?.totals.week_minor ?? 0, byAgent: own((r) => r.week_minor) };
        if (period === "this_month")
          return { total: spend?.totals.period_minor ?? 0, byAgent: own((r) => r.period_minor) };
        if (period === "last_month" || period === "this_quarter") {
          const all = await monthly(from, to);
          const byAgent = new Map<string, number>();
          let total = 0;
          for (const m of all) {
            total += m?.totals.period_minor ?? 0;
            for (const r of m?.rows ?? [])
              byAgent.set(r.membership_id, (byAgent.get(r.membership_id) ?? 0) + r.period_minor);
          }
          return { total, byAgent };
        }
        // last_week, last_30_days: the trend alone (company totals; no per-agent days).
        return { total: sumTrend(from, to), byAgent: null };
      };
      const now_ = await figures(spec.period, w.from, w.to);
      const prev =
        spec.period === "last_week" || spec.period === "last_30_days" || spec.period === "today"
          ? sumTrend(w.prevFrom, w.prevTo)
          : null;
      const names = new Map(rows.map((r) => [r.membership_id, r] as const));
      let note: string | null = spend?.computed_at
        ? null
        : "No tagged agent spend yet: spend shows once cost tagging is on.";
      let out: Row[] = [];
      if (spec.by === "agent") {
        if (now_.byAgent)
          out = [...now_.byAgent]
            .map(([id, value]) => {
              const r = names.get(id);
              return {
                key: `member:${id}`,
                label: r?.name ?? "Former agent",
                kind: (r?.kind === "agent" ? "ag" : "de") as Row["kind"],
                value,
                detail: null,
              };
            })
            .sort((a, b) => b.value - a.value);
        else note = note ?? "Per-agent figures for this period are not kept here.";
      } else if (history) {
        const days = inWindow(w.from, w.to);
        if (spec.by === "day")
          out = days.map((t) => ({
            key: t.day,
            label: t.day,
            kind: null,
            value: t.amount_minor,
            detail: null,
          }));
        else {
          const weeks = new Map<string, number>();
          for (const t of days) {
            const k = dayKey(startOfWeek(new Date(`${t.day}T00:00:00Z`)));
            weeks.set(k, (weeks.get(k) ?? 0) + t.amount_minor);
          }
          out = [...weeks].map(([k, value]) => ({
            key: k,
            label: k,
            kind: null,
            value,
            detail: null,
          }));
        }
      }
      return {
        ...base,
        currency,
        from: dayKey(w.from),
        to: dayKey(w.to),
        total: now_.total,
        previous_total: prev,
        rows: out,
        note,
      };
    }
    if (spec.measure === "approvals_waiting") {
      const list = await read<{ data: unknown[] }>(
        `/v1/companies/${co}/approvals?state=pending&limit=200`,
      );
      const n = list?.data.length ?? 0;
      return {
        ...base,
        from: dayKey(now),
        to: dayKey(now),
        total: n,
        previous_total: null,
        rows: history
          ? [{ key: dayKey(now), label: dayKey(now), kind: null, value: n, detail: null }]
          : [],
      };
    }
    // Tasks.
    const tasks = deps.tasksOf(co);
    const holders = await holdersBySeat(co);
    const at = (iso: string | null | undefined) => (iso ? new Date(iso).getTime() : null);
    const inWin = (t: number | null, from: Date, to: Date) =>
      t !== null && t >= from.getTime() && t < Math.max(to.getTime(), from.getTime() + 1);
    const match = (t: StoredTask, from: Date, to: Date) => {
      switch (spec.measure) {
        case "tasks_done":
          return inWin(at(t.completed_at), from, to);
        case "tasks_created":
          return inWin(at(t.created_at), from, to);
        case "tasks_open":
          return !t.completed_at;
        case "tasks_in_progress":
          return !t.completed_at && /progress|doing|review|started/i.test(t.status);
        default:
          return !t.completed_at && (at(t.due_at) ?? Number.POSITIVE_INFINITY) < now.getTime();
      }
    };
    const hit = tasks.filter((t) => match(t, w.from, w.to));
    const flow = def.kind === "flow";
    const prev = flow ? tasks.filter((t) => match(t, w.prevFrom, w.prevTo)).length : null;
    const whoOf = (t: StoredTask) =>
      t.assignee_seat_ids
        .flatMap((s) => holders.get(s) ?? [])
        .filter((h, i, all) => all.findIndex((x) => x.membership_id === h.membership_id) === i);
    let rows: Row[] = [];
    if (spec.by === "assignee_kind") {
      const n = { person: 0, de: 0, ag: 0, unassigned: 0 };
      for (const t of hit) {
        const kinds = new Set(whoOf(t).map((h) => kindOf(h.membership_kind)));
        if (kinds.size === 0) n.unassigned++;
        for (const k of kinds) if (k) n[k]++;
      }
      rows = (["person", "de", "ag", "unassigned"] as const)
        .filter((k) => k !== "unassigned" || n[k] > 0)
        .map((k) => ({
          key: `kind:${k}`,
          label: KIND_LABEL[k],
          kind: k,
          value: n[k],
          detail: null,
        }));
    } else if (spec.by === "assignee") {
      const by = new Map<string, Row>();
      for (const t of hit) {
        const who = whoOf(t);
        if (who.length === 0) {
          const r = by.get("kind:unassigned") ?? {
            key: "kind:unassigned",
            label: "Unassigned",
            kind: "unassigned" as const,
            value: 0,
            detail: null,
          };
          r.value++;
          by.set(r.key, r);
        }
        for (const h of who) {
          const key = `member:${h.membership_id}`;
          const r = by.get(key) ?? {
            key,
            label: h.display_name,
            kind: kindOf(h.membership_kind),
            value: 0,
            detail: null,
          };
          r.value++;
          by.set(key, r);
        }
      }
      rows = [...by.values()].sort((a, b) => b.value - a.value).slice(0, 26);
    } else if (history) {
      const days: string[] = [];
      for (let d = startOfDay(w.from).getTime(); d <= w.to.getTime(); d += DAY)
        days.push(dayKey(new Date(d)));
      const count = (day: string) =>
        flow
          ? hit.filter(
              (t) =>
                (spec.measure === "tasks_created" ? t.created_at : t.completed_at)?.slice(0, 10) ===
                day,
            ).length
          : day === dayKey(now)
            ? hit.length
            : 0;
      const shownDays = flow ? days : [dayKey(now)];
      if (spec.by === "day")
        rows = shownDays.map((d) => ({
          key: d,
          label: d,
          kind: null,
          value: count(d),
          detail: null,
        }));
      else {
        const weeks = new Map<string, number>();
        for (const d of shownDays) {
          const wk = dayKey(startOfWeek(new Date(`${d}T12:00:00Z`)));
          weeks.set(wk, (weeks.get(wk) ?? 0) + count(d));
        }
        rows = [...weeks].map(([k, v]) => ({
          key: k,
          label: `W/${k.slice(5)}`,
          kind: null,
          value: v,
          detail: null,
        }));
      }
    }
    return {
      ...base,
      from: dayKey(flow || history ? w.from : now),
      to: dayKey(now),
      total: hit.length,
      previous_total: prev,
      rows,
      note: !flow && history ? "History starts on the first daily snapshot." : null,
    };
  }

  function answerText(r: Result): string {
    const fmt = (v: number) =>
      r.unit === "money"
        ? new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: r.currency ?? "USD",
          }).format(v / 100)
        : r.unit === "percent"
          ? `${Math.round(v)}%`
          : String(v);
    const parts = [r.total === null ? `${r.title}.` : `${r.title}: ${fmt(r.total)}.`];
    const hist = r.spec.by === "day" || r.spec.by === "week";
    if (r.rows.length > 0 && !hist)
      parts.push(
        `${r.rows
          .slice(0, 4)
          .map((x) => `${x.label} ${fmt(x.value)}`)
          .join(" · ")}.`,
      );
    if (r.note) parts.push(r.note);
    return parts.join(" ");
  }

  const specOf = (raw: unknown): Spec | Response => {
    const s = raw as Partial<Spec> | null;
    if (!s?.measure || !(s.measure in CATALOG)) return validation("measure: not in the catalogue");
    if (s.by && s.by !== "none" && !CATALOG[s.measure].by.includes(s.by))
      return validation("by: this breakdown is not offered for this measure");
    if (s.period && !PERIODS.includes(s.period)) return validation("period: unknown");
    return normal({ measure: s.measure, by: s.by, period: s.period });
  };

  const handlers: Partial<Record<RouteId, ComposedHandler>> = {
    "insights.catalog": (ctx) => {
      if (!ctx.principal?.manager) return forbidden();
      return json({
        measures: (Object.keys(CATALOG) as Measure[]).map((measure) => {
          const d = CATALOG[measure];
          return { measure, label: d.label, unit: d.unit, kind: d.kind, by: d.by, info: d.info };
        }),
        periods: PERIODS,
        examples: EXAMPLES,
      });
    },
    "insights.query": async (ctx) => {
      if (!ctx.principal?.manager) return forbidden();
      const p = ctx.url.searchParams;
      const spec = specOf({
        measure: p.get("measure"),
        by: p.get("by") ?? undefined,
        period: p.get("period") ?? undefined,
      });
      if (spec instanceof Response) return spec;
      return json(await query(ctx.principal.company.id, spec));
    },
    "insights.ask": async (ctx) => {
      const p = ctx.principal;
      if (!p?.manager) return forbidden();
      const me = meIn(ctx);
      if (!me) return forbidden();
      const body = ctx.body as OperationInput<"insights.ask">["body"];
      const question = String(body?.question ?? "").trim();
      if (!question || question.length > 500) return validation("question: 1–500 characters");
      const spec = parse(question);
      const result = spec ? await query(p.company.id, spec) : null;
      const a: Answer = {
        id: uid(0x1a5c, ++seq),
        question,
        answer: result
          ? answerText(result)
          : "That is not something Ask can measure yet. It answers spend, budgets, tasks and approvals; try one of these.",
        via: spec ? "rules" : "none",
        spec,
        result,
        suggestions: result ? [] : EXAMPLES.slice(0, 4),
        created_at: deps.now().toISOString(),
      };
      asks.push({ ...a, company_id: p.company.id, membership_id: me });
      return json(a);
    },
    "insights.asks": (ctx) => {
      const p = ctx.principal;
      if (!p?.manager) return forbidden();
      const me = meIn(ctx);
      const mine = asks.filter((a) => a.company_id === p.company.id && a.membership_id === me);
      return json({
        data: mine
          .slice()
          .reverse()
          .slice(0, 50)
          .map(({ result: _r, company_id: _c, membership_id: _m, ...a }) => a),
        left_today: Math.max(0, 50 - mine.length),
      });
    },
    "insights.widgets.list": async (ctx) => {
      const p = ctx.principal;
      if (!p?.manager) return forbidden();
      const me = meIn(ctx);
      const mine = widgets.filter(
        (x) =>
          x.company_id === p.company.id &&
          (x.owner.membership_id === me || x.visibility === "admins"),
      );
      return json({
        data: mine.map(({ company_id: _c, ...x }) => ({
          ...x,
          can_remove: x.owner.membership_id === me || x.visibility === "admins",
        })),
      });
    },
    "insights.widgets.create": async (ctx) => {
      const p = ctx.principal;
      if (!p?.manager) return forbidden();
      const me = meIn(ctx);
      if (!me) return forbidden();
      const body = ctx.body as OperationInput<"insights.widgets.create">["body"];
      const title = String(body?.title ?? "").trim();
      if (!title || title.length > 120) return validation("title: 1–120 characters");
      const spec = specOf(body?.spec);
      if (spec instanceof Response) return spec;
      // The API's caps: 12 pinned for oneself, 12 for every admin (company-wide).
      const visibility = body?.visibility ?? "me";
      const mine = widgets.filter((x) => x.company_id === p.company.id);
      const full =
        visibility === "me"
          ? mine.filter((x) => x.visibility === "me" && x.owner.membership_id === me).length >= 12
          : mine.filter((x) => x.visibility === "admins").length >= 12;
      if (full)
        return problem(
          409,
          "conflict",
          "Conflict",
          visibility === "me"
            ? "Home holds 12 answers pinned for you; unpin one first."
            : "Home holds 12 answers pinned for every admin; unpin one first.",
        );
      const history = spec.by === "day" || spec.by === "week";
      const now = deps.now().toISOString();
      const name = ctx.me.user.name || ctx.me.user.email;
      const w: Widget & { company_id: string } = {
        id: uid(0x1a5d, ++seq),
        title,
        spec,
        viz: body?.viz ?? (history ? "line" : spec.by === "none" ? "number" : "bars"),
        visibility: body?.visibility ?? "me",
        owner: { membership_id: me, name },
        can_remove: true,
        result: await query(p.company.id, spec),
        refreshed_at: now,
        created_at: now,
        company_id: p.company.id,
      };
      widgets.push(w);
      const { company_id: _c, ...out } = w;
      return json(out, 201);
    },
    "insights.widgets.delete": (ctx) => {
      const p = ctx.principal;
      if (!p?.manager) return forbidden();
      const me = meIn(ctx);
      const i = widgets.findIndex(
        (x) =>
          x.company_id === p.company.id &&
          x.id === ctx.params.widget_id &&
          (x.owner.membership_id === me || x.visibility === "admins"),
      );
      if (i < 0) return problem(404, "not_found", "Not found");
      widgets.splice(i, 1);
      return new Response(null, { status: 204 });
    },
  };
  return { handlers };
}
